import { AnalyticsEvents, trackEvent } from 'app/shared/u21-ui/analytics';

import {
  forwardRef,
  HTMLProps,
  ReactElement,
  ReactNode,
  RefObject,
  SyntheticEvent,
} from 'react';
import {
  convertStyleObjectToCSSString,
  getDOMProps,
} from 'app/shared/utils/react';
import styled from 'styled-components';

import { Checkbox, CheckboxProps, FormControlLabel } from '@mui/material';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21Tooltip } from 'app/shared/u21-ui/components/display/U21Tooltip';

export interface U21CheckboxProps extends Omit<
  HTMLProps<HTMLButtonElement>,
  'label' | 'onChange'
> {
  checked?: boolean;
  description?: string;
  disabled?: boolean;
  ellipsis?: boolean;
  endIcon?: ReactElement;
  fullWidth?: boolean;
  indeterminate?: boolean;
  inputProps?: CheckboxProps['inputProps'];
  label?: ReactNode;
  onChange: (checked: boolean, e: SyntheticEvent) => void;
  ref?: RefObject<HTMLButtonElement>;
  required?: boolean;
  tooltip?: string;
}

export const U21Checkbox = forwardRef<HTMLButtonElement, U21CheckboxProps>(
  (props, ref) => {
    const {
      checked = false,
      description,
      disabled,
      ellipsis,
      endIcon,
      fullWidth,
      indeterminate,
      inputProps = {},
      label,
      onChange,
      required,
      tooltip,
      ...rest
    } = props;

    const checkboxProps = {
      checked,
      disabled,
      indeterminate: checked ? undefined : indeterminate,
      onChange: (e) => {
        trackEvent(AnalyticsEvents.U21CHECKBOX_ON_CLICK, props, {}, e);
        onChange(e.target.checked, e);
      },
      inputProps,
    };
    if (!label) {
      return (
        <U21Tooltip tooltip={tooltip}>
          <Checkbox ref={ref} {...checkboxProps} {...getDOMProps(rest)} />
        </U21Tooltip>
      );
    }

    return (
      <U21Tooltip tooltip={tooltip}>
        <StyledFormControlLabel
          $fullWidth={fullWidth}
          control={<Checkbox {...checkboxProps} />}
          disableTypography
          label={
            <LabelContainer horizontal useFlexGap>
              <LabelContent>
                {typeof label === 'string' ? (
                  <U21Typography
                    color={disabled ? 'text.disabled' : 'text.primary'}
                    ellipsis={ellipsis}
                    required={required}
                    variant="body2"
                  >
                    {label}
                  </U21Typography>
                ) : (
                  label
                )}
                <U21Typography
                  color="text.disabled"
                  ellipsis={ellipsis}
                  variant="body2"
                >
                  {description}
                </U21Typography>
              </LabelContent>
              {endIcon}
            </LabelContainer>
          }
          ref={ref}
          {...getDOMProps(rest)}
        />
      </U21Tooltip>
    );
  },
);

const StyledFormControlLabel = styled(FormControlLabel)<{
  $fullWidth?: boolean;
}>`
  // ignore margin if full width since there won't be anything on the right
  margin-right: ${(props) => (props.$fullWidth ? 0 : 32)}px;
  // account for -11px margin-left default mui style
  width: ${(props) => (props.$fullWidth ? 'calc(100% + 11px)' : 'fit-content')};
  max-width: calc(100% + 11px);
  .MuiFormControlLabel-label {
    margin: 4px 0;
  }
`;

const LabelContainer = styled(U21Spacer)`
  flex: 1;
  overflow: hidden;
`;

const LabelContent = styled.div`
  overflow: hidden;
  margin: 4px 0;
  flex: 1;
  ${(props) => convertStyleObjectToCSSString(props.theme.typography.body2)};
`;
